import React from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Box, TextField } from '@mui/material';
import { EnterpriseClassification } from '@esg/esg-global-types';
import {
  enterprise_classification_label,
  getEnterpriseClassifications
} from '../../../../lib/app/enterprise_classification';

interface EnterpriseClassificationSelectProps {
  handleChangeEnterpriseClassification?: (
    enterprise_classification: EnterpriseClassification | null
  ) => void;
  selected_enterprise_classification?: EnterpriseClassification | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  variant?: 'standard' | 'outlined';
  auto_select?: boolean;
}

/**
 * Enterprise Classification selection auto-complete component that handles a variety of built-in scenarios.
 * @param {EnterpriseClassification | null} props.handleChangeEnterpriseClassification Callback function to notify the parent component when the selected options have changed.
 * @param {EnterpriseClassification} props.selected_enterprise_classification Enterprise Classfication for the group provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete
 * @param {boolean} props.auto_select Pre-populate the autocomplete input when only one enterprise classification is available.
 */
const EnterpriseClassificationSelect = (props: EnterpriseClassificationSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<EnterpriseClassification>>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<EnterpriseClassification | null>(null);

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const options: Array<EnterpriseClassification> = await getEnterpriseClassifications();
      if (options) setOptions(options);
      // When auto-select is true, automatically populate the autocomplete when only one Enterprise Classification is present.
      if (props.auto_select && options.length === 1 && options[0] !== selectedOption) {
        setSelectedOption(options[0]);
        props.handleChangeEnterpriseClassification &&
          props.handleChangeEnterpriseClassification(options[0]);
      }
      setLoading(false);
    })();
  }, []);
  // Define selected options
  React.useEffect(() => {
    setSelectedOption(
      props.selected_enterprise_classification ? props.selected_enterprise_classification : null
    );
  }, [props.selected_enterprise_classification]);

  return (
    <Box id="enterprise-classification-select">
      <Autocomplete
        fullWidth
        disabled={props.disabled}
        loading={loading}
        options={optionList}
        value={selectedOption}
        disableClearable={props.clearable === false}
        getOptionLabel={(option: EnterpriseClassification) => {
          return option.name;
        }}
        isOptionEqualToValue={(option: EnterpriseClassification, value: EnterpriseClassification) =>
          option.id === value.id
        }
        noOptionsText={`No ${enterprise_classification_label.many.toLowerCase()}`}
        renderOption={(render_props, option: EnterpriseClassification) => (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...render_props}
            key={option.id}
          >
            {option.name}
          </Box>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={props.input_label ?? enterprise_classification_label.one}
            variant={props.variant ?? 'standard'}
          />
        )}
        onChange={(
          event: React.SyntheticEvent<Element, Event>,
          value: EnterpriseClassification | null
        ) => {
          setSelectedOption(value);
          props.handleChangeEnterpriseClassification &&
            props.handleChangeEnterpriseClassification(value);
        }}
      />
    </Box>
  );
};

export default EnterpriseClassificationSelect;
